import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { FilePdf, Download, Printer, DownloadSimple } from "@phosphor-icons/react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AdminPayslipGenerator() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);

  const params = () => new URLSearchParams({ year, month }).toString();

  const doAction = async (action) => {
    if (!employeeId) { toast.error("Pick an employee first"); return; }
    setBusy(true);
    try {
      const res = await api.get(`/employees/${employeeId}/payslip?${params()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const emp = employees.find((e) => e.id === employeeId);
      const filename = `payslip_${emp?.employee_id || employeeId}_${year}_${String(month).padStart(2,"0")}.pdf`;
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
      } else {
        const w = window.open(url, "_blank");
        if (w) w.onload = () => w.print();
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Could not generate payslip");
    } finally { setBusy(false); }
  };

  const downloadAll = async () => {
    setBulkBusy(true);
    try {
      const res = await api.get(`/payroll/payslips.zip?${params()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslips_${year}_${String(month).padStart(2,"0")}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Zipped ${employees.length} payslips for ${MONTHS[month - 1]} ${year}`);
    } catch (err) {
      toast.error("Bulk download failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E7EB]" data-testid="admin-payslip-generator">
      <div className="px-4 sm:px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FilePdf size={16} color="#106109" weight="fill" />
          <div>
            <div className="label-caps">Payslip Generator</div>
            <div className="font-display font-bold text-xl mt-1">Month-wise · Employee-wise Payslip</div>
          </div>
        </div>
        <button onClick={downloadAll} disabled={bulkBusy}
                data-testid="ap-download-all"
                className="border border-[#106109] text-[#106109] hover:bg-[#F0F7EF] px-3 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60 font-semibold">
          <DownloadSimple size={14} weight="bold" />
          {bulkBusy ? "Zipping…" : `Download all for ${MONTHS[month - 1]} ${year} (ZIP)`}
        </button>
      </div>
      <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="label-caps block mb-1.5">Employee</label>
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                  data-testid="ap-employee" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm">
            <option value="">— Select —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_id} — {e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">Month</label>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                  data-testid="ap-month" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">Year</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                  data-testid="ap-year" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm">
            {[now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <button onClick={() => doAction("download")} disabled={busy || !employeeId}
                  data-testid="ap-download"
                  className="brand-btn flex-1 py-2 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            <Download size={14} weight="bold" /> {busy ? "…" : "Download"}
          </button>
          <button onClick={() => doAction("print")} disabled={busy || !employeeId}
                  data-testid="ap-print"
                  className="border border-[#106109] text-[#106109] px-3 py-2 text-sm inline-flex items-center gap-1.5 hover:bg-[#F0F7EF] disabled:opacity-60">
            <Printer size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
